import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useAdmin } from '../../hooks/useAdmin'
import { supabase } from '../../services/supabase'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../utils/constants'

export type CompanyUserMember = {
  userId: string
  email: string | null
  name: string | null
  role: string
  linkedAt: string
}

type AccessMode = 'sso' | 'email_password'

type TempPasswordInfo = {
  email: string
  tempPassword: string
  emailSent: boolean
}

async function callCompanyUsers(pathQuery: string, init: RequestInit = {}) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not signed in')
  const url = `${SUPABASE_URL}/functions/v1/admin-company-users${pathQuery}`
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.error || json?.message || `HTTP ${res.status}`)
  }
  return json
}

type Props = {
  initialCompanyId?: string | null
  onCompanyChange?: (companyId: string) => void
  /** Compact layout for embedding in Enable SSO wizard. */
  embedded?: boolean
  /** Hide company dropdown (parent already selected the company). */
  lockCompany?: boolean
}

/**
 * Tenants → Company users: list active members, add by email, unlink.
 * Also supports temporary email+password issuance when SSO is blocked.
 */
export default function CompanyUsersPanel({
  initialCompanyId,
  onCompanyChange,
  embedded = false,
  lockCompany = false,
}: Props) {
  const { companies, companiesLoading } = useAdmin()
  const [companyId, setCompanyId] = useState(initialCompanyId || '')
  const [members, setMembers] = useState<CompanyUserMember[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [accessMode, setAccessMode] = useState<AccessMode>('sso')
  const [saving, setSaving] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [issuingEmail, setIssuingEmail] = useState<string | null>(null)
  const [tempInfo, setTempInfo] = useState<TempPasswordInfo | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (initialCompanyId) setCompanyId(initialCompanyId)
  }, [initialCompanyId])

  useEffect(() => {
    if (!companyId && companies?.length) {
      setCompanyId(companies[0].id)
    }
  }, [companies, companyId])

  const load = useCallback(async (id: string) => {
    if (!id) {
      setMembers([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await callCompanyUsers(`?company_id=${encodeURIComponent(id)}`, { method: 'GET' })
      setMembers((data.members as CompanyUserMember[]) || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users')
      setMembers([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (companyId) void load(companyId)
  }, [companyId, load])

  const handleCompanySelect = (id: string) => {
    setCompanyId(id)
    setSuccess(null)
    setError(null)
    setTempInfo(null)
    onCompanyChange?.(id)
  }

  const applyTempPasswordResult = (data: {
    email?: string
    tempPassword?: string
    emailSent?: boolean
    message?: string
  }) => {
    if (data.tempPassword && data.email) {
      setTempInfo({
        email: data.email,
        tempPassword: data.tempPassword,
        emailSent: Boolean(data.emailSent),
      })
      setCopied(false)
    }
    setSuccess(data.message || 'Temporary email sign-in ready.')
  }

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault()
    if (!companyId || !email.trim()) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    if (accessMode === 'sso') setTempInfo(null)
    try {
      const data = await callCompanyUsers('', {
        method: 'POST',
        body: JSON.stringify({
          companyId,
          email: email.trim(),
          name: name.trim() || undefined,
          accessMode,
        }),
      })
      if (accessMode === 'email_password') {
        applyTempPasswordResult(data)
      } else {
        setSuccess(data.message || 'User linked to company.')
      }
      setEmail('')
      setName('')
      await load(companyId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add user')
    } finally {
      setSaving(false)
    }
  }

  const handleIssueTempPassword = async (memberEmail: string) => {
    if (!companyId || !memberEmail) return
    if (
      !confirm(
        `This replaces any existing password for ${memberEmail}. The previous password stops working immediately. Continue?`
      )
    ) {
      return
    }
    setIssuingEmail(memberEmail)
    setError(null)
    setSuccess(null)
    try {
      const data = await callCompanyUsers('', {
        method: 'POST',
        body: JSON.stringify({
          companyId,
          email: memberEmail,
          accessMode: 'email_password',
        }),
      })
      applyTempPasswordResult(data)
      await load(companyId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to issue temp password')
    } finally {
      setIssuingEmail(null)
    }
  }

  const handleCopyPassword = async () => {
    if (!tempInfo?.tempPassword) return
    try {
      await navigator.clipboard.writeText(tempInfo.tempPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Could not copy to clipboard. Select and copy the password manually.')
    }
  }

  const handleRemove = async (userId: string, memberEmail: string | null) => {
    if (!companyId) return
    const label = memberEmail || userId.slice(0, 8)
    if (!confirm(`Remove ${label} from this company? They will lose company data access.`)) return
    setRemovingId(userId)
    setError(null)
    setSuccess(null)
    try {
      await callCompanyUsers('', {
        method: 'DELETE',
        body: JSON.stringify({ companyId, userId }),
      })
      setSuccess('User removed from this company.')
      await load(companyId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user')
    } finally {
      setRemovingId(null)
    }
  }

  const selectedName = companies.find((c) => c.id === companyId)?.name
  const selectedDomain = companies.find((c) => c.id === companyId)?.domain

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-4 sm:space-y-6'}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {!embedded && (
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-bold">
              <i className="fa-solid fa-user-group mr-2"></i>Company users
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              See who has access to a company and add people by work email. Prefer SSO once the
              customer&apos;s Microsoft/Google/IAM setup is ready; use temporary email sign-in only
              while SSO is blocked.
            </p>
          </div>
        )}
        <div className="p-4 sm:p-6 space-y-4">
          {!lockCompany && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
              <select
                value={companyId}
                onChange={(e) => handleCompanySelect(e.target.value)}
                disabled={companiesLoading || !companies.length}
                className="w-full max-w-md p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
              >
                {!companies.length && <option value="">No companies</option>}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.domain ? ` (${c.domain})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded">
              {success}
            </div>
          )}

          {tempInfo && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm space-y-2">
              <div className="font-semibold text-yellow-900">Temporary email sign-in</div>
              <div className="text-gray-700 break-all">
                <strong>Email:</strong> {tempInfo.email}
              </div>
              <div className="text-gray-700 break-all">
                <strong>Temp password:</strong>{' '}
                <code className="font-mono bg-yellow-100 px-2 py-1 rounded">{tempInfo.tempPassword}</code>
              </div>
              {!tempInfo.emailSent && (
                <p className="text-amber-800 text-xs">
                  Email not sent — copy and share this password manually.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleCopyPassword()}
                  className="px-3 py-1.5 text-xs font-medium bg-black text-white rounded hover:bg-gray-800"
                >
                  {copied ? 'Copied' : 'Copy password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTempInfo(null)
                    setCopied(false)
                  }}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">Access mode</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAccessMode('sso')}
                className={`px-3 py-1.5 text-sm rounded-md border font-medium ${
                  accessMode === 'sso'
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                SSO
              </button>
              <button
                type="button"
                onClick={() => setAccessMode('email_password')}
                className={`px-3 py-1.5 text-sm rounded-md border font-medium ${
                  accessMode === 'email_password'
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Temporary email + password
              </button>
            </div>
            {accessMode === 'email_password' ? (
              <p className="mt-2 text-xs text-gray-500">
                Temporary until this company&apos;s Microsoft/Google/IAM SSO works
                {selectedDomain ? ` (email must be @${selectedDomain})` : ''}. User must change
                password on first sign-in. Prefer the Microsoft button once admin consent is granted.
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500">
                Links the work email for Google, Microsoft, or Company SSO. Does not show a password.
              </p>
            )}
          </div>

          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Work email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@customer.com"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name (optional)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Display name"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={saving || !companyId || !email.trim()}
              className="px-4 py-3 bg-black text-white rounded-md font-medium hover:bg-gray-800 disabled:opacity-50 whitespace-nowrap"
            >
              {saving
                ? accessMode === 'email_password'
                  ? 'Creating...'
                  : 'Adding...'
                : accessMode === 'email_password'
                  ? 'Create email sign-in'
                  : 'Add user'}
            </button>
          </form>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base sm:text-lg font-semibold">
            Active users
            {selectedName ? (
              <span className="font-normal text-gray-500"> — {selectedName}</span>
            ) : null}
            <span className="ml-2 text-sm font-normal text-gray-500">({members.length})</span>
          </h3>
          <button
            type="button"
            onClick={() => companyId && load(companyId)}
            className="text-sm text-blue-600 hover:underline font-medium"
            disabled={loading || !companyId}
          >
            <i className="fa-solid fa-arrow-rotate-right mr-1"></i>Refresh
          </button>
        </div>
        <div className="p-4 sm:p-6 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">
              <i className="fa-solid fa-spinner fa-spin mr-2"></i>Loading...
            </div>
          ) : !companyId ? (
            <div className="p-8 text-center text-gray-400">Select a company.</div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              No users on this company yet. Add a work email above.
            </div>
          ) : (
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 font-semibold">
                <tr>
                  <th className="p-3 sm:p-4">Email</th>
                  <th className="p-3 sm:p-4">Name</th>
                  <th className="p-3 sm:p-4">Role</th>
                  <th className="p-3 sm:p-4 hidden sm:table-cell">Linked</th>
                  <th className="p-3 sm:p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-3 sm:p-4 font-medium break-all">{m.email || '—'}</td>
                    <td className="p-3 sm:p-4 text-gray-600">{m.name || '—'}</td>
                    <td className="p-3 sm:p-4 text-gray-600">{m.role}</td>
                    <td className="p-3 sm:p-4 text-gray-500 hidden sm:table-cell">
                      {m.linkedAt ? new Date(m.linkedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <button
                          type="button"
                          onClick={() => m.email && void handleIssueTempPassword(m.email)}
                          disabled={!m.email || issuingEmail === m.email}
                          className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-40 disabled:no-underline text-left"
                          title={!m.email ? 'Email required to issue a password' : undefined}
                        >
                          {issuingEmail === m.email ? 'Issuing...' : 'Issue temp password'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(m.userId, m.email)}
                          disabled={removingId === m.userId}
                          className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50 text-left"
                        >
                          {removingId === m.userId ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export async function fetchCompanyUserCounts(): Promise<Record<string, number>> {
  const data = await callCompanyUsers('', { method: 'GET' })
  return (data.counts as Record<string, number>) || {}
}
